import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api, fileURL } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Map as MapIcon, Filter, MapPin, Camera, Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { OwnTrackingNotice } from '../../components/OwnTrackingNotice';

const listVariants = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', bounce: 0, duration: 0.3 } },
};

const BRAND_STYLE = {
  UNO:    { bg: 'bg-[#fef3c7]', fg: 'text-[#92400e]', code: 'UN' },
  Shell:  { bg: 'bg-[#dbeafe]', fg: 'text-[#1e40af]', code: 'SH' },
  Puma:   { bg: 'bg-[#dbeafe]', fg: 'text-[#1d4ed8]', code: 'PU' },
  Texaco: { bg: 'bg-[#fef3c7]', fg: 'text-[#b45309]', code: 'TE' },
  Esso:   { bg: 'bg-[#fee2e2]', fg: 'text-[#991b1b]', code: 'ES' },
  Other:  { bg: 'bg-[#f3f0e9]', fg: 'text-[#3a3a3a]', code: '··' },
};

const brandPill = (brand) => BRAND_STYLE[brand] || BRAND_STYLE.Other;

const STATUS_PILL = {
  pendiente:  { label: 'Pendiente',  cls: 'bg-[#f3f0e9] text-[#3a3a3a]', dot: 'bg-[#a8a397]' },
  registrada: { label: 'Registrada', cls: 'bg-[#dcfce7] text-[#166534]', dot: 'bg-[#15803d]' },
};

const weekdayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('es-GT', { weekday: 'long', day: '2-digit', month: 'short' }).toUpperCase();
};

const MobileRoute = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ date: '', stations: [] });
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');     // all | pendiente | registrada
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [search, setSearch] = useState('');

  // Geolocation (one-shot at mount, refresh on demand)
  const getCoords = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {/* silently fall back to no-GPS */},
      { timeout: 8000, enableHighAccuracy: false, maximumAge: 60000 },
    );
  }, []);

  useEffect(() => { getCoords(); }, [getCoords]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = coords ? { lat: coords.lat, lng: coords.lng } : {};
      const r = await api.get('/my-route', { params });
      setData(r.data || { date: '', stations: [] });
    } catch {
      toast.error('No se pudo cargar la ruta');
    } finally { setLoading(false); }
  }, [coords]);

  useEffect(() => { load(); const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  const [brandLogos, setBrandLogos] = useState({});
  useEffect(() => {
    api.get('/brands').then(r => {
      const map = {};
      (r.data || []).forEach(b => { if (b.logo_url) map[b.name.toLowerCase()] = fileURL(b.logo_url); });
      setBrandLogos(map);
    }).catch(() => {});
  }, []);


  const brands = useMemo(() => Array.from(new Set((data.stations || []).map(s => s.brand).filter(Boolean))).sort(), [data.stations]);
  const regions = useMemo(() => Array.from(new Set((data.stations || []).map(s => s.region).filter(Boolean))).sort(), [data.stations]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.stations || []).filter(s => {
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (filterBrand !== 'all' && s.brand !== filterBrand) return false;
      if (filterRegion !== 'all' && s.region !== filterRegion) return false;
      if (q && !(`${s.name} ${s.brand} ${s.address} ${s.region}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [data.stations, filterStatus, filterBrand, filterRegion, search]);

  const total = data.stations?.length || 0;
  // Las PROPIAS no se capturan, salvo las que son marker de otra propia (dealer)
  const capturables = useMemo(() => (data.stations || []).filter(s => !s.is_own || s.trackable_as_marker), [data.stations]);
  const totalCap = capturables.length;
  const registradas = capturables.filter(s => s.status === 'registrada').length;
  const progress = totalCap > 0 ? Math.round((registradas / totalCap) * 100) : 0;

  const goCapture = (s) => navigate(s.is_own && !s.trackable_as_marker ? `/app/station/${s.id}` : `/app/capture/${s.id}`);
  const goNextPending = () => {
    const next = capturables.find(s => s.status === 'pendiente');
    if (next) navigate(`/app/capture/${next.id}`);
    else toast.success('¡Ruta completada! 🎉');
  };

  const resetFilters = () => {
    setFilterStatus('all'); setFilterBrand('all'); setFilterRegion('all'); setSearch('');
  };
  const filtersActive = (filterStatus !== 'all') || (filterBrand !== 'all') || (filterRegion !== 'all') || !!search.trim();

  return (
    <div className="p-4 space-y-4 pb-28" data-testid="mobile-route-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#8a8579]" data-testid="route-date">
            {weekdayLabel(data.date)}
          </p>
          <h1 className="font-serif text-[28px] leading-tight tracking-tight">Mi ruta</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setShowFilters(v => !v)}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition border ${filtersActive ? 'bg-[#1c1c1c] border-[#1c1c1c] text-white' : 'bg-white border-[#e8e5de] text-[#3a3a3a] hover:bg-[#f3f0e9]'}`}
            data-testid="route-toggle-filters"
            aria-label="Filtros"
          >
            <Filter size={15} strokeWidth={1.8}/>
          </button>
          <button
            type="button"
            onClick={() => navigate('/app/map')}
            className="w-9 h-9 rounded-lg flex items-center justify-center bg-white border border-[#e8e5de] text-[#3a3a3a] hover:bg-[#f3f0e9] transition"
            data-testid="route-go-map"
            aria-label="Ver mapa"
          >
            <MapIcon size={15} strokeWidth={1.8}/>
          </button>
        </div>
      </div>

      {/* Aviso: ya no se trackean estaciones propias */}
      <OwnTrackingNotice />

      {/* Progress */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <p className="text-[12.5px] text-[#3a3a3a]" data-testid="route-progress-text">
            <span className="font-medium">{registradas}</span> de <span className="font-medium">{totalCap}</span> estaciones
          </p>
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579]" data-testid="route-progress-pct">{progress}%</p>
        </div>
        <div className="h-1 bg-[#f0ede5] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#15803d] transition-all duration-500"
            style={{ width: `${progress}%` }}
            data-testid="route-progress-bar"
          />
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-3 space-y-3" data-testid="route-filters-panel">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-wider text-[#8a8579]">Filtros</p>
            {filtersActive && (
              <button onClick={resetFilters} className="text-[11px] text-[#3a3a3a] underline" data-testid="route-filters-reset">Limpiar</button>
            )}
          </div>
          <Input
            placeholder="Buscar nombre, dirección, zona…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 text-[12.5px]"
            data-testid="route-filter-search"
          />
          {/* Status segmented */}
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { k: 'all',        label: 'Todas' },
              { k: 'pendiente',  label: 'Pendientes' },
              { k: 'registrada', label: 'Registradas' },
            ].map(o => (
              <button
                key={o.k}
                onClick={() => setFilterStatus(o.k)}
                className={`press h-8 rounded-full text-[11.5px] border transition-colors duration-150 ${filterStatus === o.k ? 'bg-[#1c1c1c] text-white border-[#1c1c1c]' : 'bg-white text-[#3a3a3a] border-[#e8e5de] hover:bg-[#f3f0e9]'}`}
                data-testid={`route-filter-status-${o.k}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {/* Brand + region */}
          <div className="grid grid-cols-2 gap-2">
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="h-9 text-[12.5px] border border-[#e8e5de] rounded-md px-2 bg-white"
              data-testid="route-filter-brand"
            >
              <option value="all">Todas las marcas</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="h-9 text-[12.5px] border border-[#e8e5de] rounded-md px-2 bg-white"
              data-testid="route-filter-region"
            >
              <option value="all">Todas las zonas</option>
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div>
      ) : total === 0 ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-10 text-center">
          <MapPin size={28} strokeWidth={1.4} className="mx-auto text-[#a8a397] mb-2"/>
          <p className="text-[13px] text-[#8a8579]">No tienes estaciones asignadas en tu ruta.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center">
          <p className="text-[13px] text-[#8a8579]">Sin resultados con estos filtros.</p>
          <button onClick={resetFilters} className="text-[12px] text-[#1c1c1c] underline mt-2" data-testid="route-empty-reset">Limpiar filtros</button>
        </div>
      ) : (
        <motion.div className="space-y-2.5" data-testid="route-list" variants={listVariants} initial="hidden" animate="show">
          {filtered.map((s, idx) => {
            const bp = brandPill(s.brand);
            const logo = brandLogos[(s.brand || '').toLowerCase()];
            const sp = STATUS_PILL[s.status] || STATUS_PILL.pendiente;
            const isDone = (!s.is_own || s.trackable_as_marker) && s.status === 'registrada';
            return (
              <motion.button
                key={s.id}
                variants={itemVariants}
                whileTap={{ scale: 0.98 }}
                onClick={() => goCapture(s)}
                className={`w-full text-left bg-white border rounded-2xl p-3 flex gap-3 items-center transition-shadow hover:shadow-[0_4px_12px_rgba(28,28,28,0.06),0_1px_2px_rgba(28,28,28,0.04)] ${isDone ? 'border-[#e8e5de] opacity-80' : 'border-[#e8e5de]'}`}
                data-testid={`route-card-${s.id}`}
              >
                {/* Brand chip with order number */}
                <div className="relative shrink-0">
                  {logo ? (
                    <div className="w-12 h-12 rounded-xl bg-white border border-[#e8e5de] flex items-center justify-center overflow-hidden">
                      <img src={logo} alt={s.brand} className="w-9 h-9 object-contain"/>
                    </div>
                  ) : (
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bp.bg}`}>
                      <span className={`font-semibold tracking-tight text-[15px] ${bp.fg}`}>{bp.code}</span>
                    </div>
                  )}
                  <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-[#1c1c1c] text-white text-[10px] font-medium flex items-center justify-center" data-testid={`route-order-${s.id}`}>
                    {idx + 1}
                  </span>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className={`text-[13.5px] leading-tight font-medium ${isDone ? 'line-through text-[#8a8579]' : 'text-[#1c1c1c]'} truncate`}>
                      {s.brand} — {s.name}
                    </p>
                    {s.role === 'marker' && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b] shrink-0" data-testid={`route-marker-tag-${s.id}`}>
                        Marker
                      </span>
                    )}
                    {s.is_own && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#e7eeff] text-[#0f1d6f] shrink-0" data-testid={`route-own-tag-${s.id}`}>
                        Propia
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#8a8579] mt-0.5 flex items-center gap-1 min-w-0">
                    <MapPin size={10} className="shrink-0"/> <span className="truncate">{s.address || s.region}</span>
                    {s.distance_km != null && <span className="text-[#a8a397] shrink-0">· {s.distance_km} km</span>}
                  </p>
                  {s.role === 'marker' && s.marker_of && s.marker_of.length > 0 && (
                    <p className="text-[10.5px] text-[#5a5a5a] mt-0.5 truncate">Marker de {s.marker_of.join(' · ')}</p>
                  )}
                  {s.is_own && !s.trackable_as_marker ? (
                    <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10.5px] bg-[#f3f0e9] text-[#5a5a5a]" data-testid={`route-status-${s.id}`}>
                      Precios vigentes = últimos autorizados
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10.5px] ${sp.cls}`} data-testid={`route-status-${s.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${sp.dot}`}/> {sp.label}
                    </span>
                  )}
                </div>
                {/* Time */}
                <div className="shrink-0 text-right">
                  <p className="font-mono text-[11px] text-[#8a8579] tabular-nums" data-testid={`route-time-${s.id}`}>
                    {s.capture_time || s.next_slot || (s.schedule_today?.[0]) || '—'}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      )}

      {/* Floating "Capturar" FAB → go to next pending */}
      {totalCap > 0 && (
        <motion.button
          onClick={goNextPending}
          whileTap={{ scale: 0.9 }}
          transition={{ type: 'spring', bounce: 0, duration: 0.2 }}
          className="fixed left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-1 group"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 9rem)' }}
          data-testid="route-fab-capture"
          aria-label="Capturar próxima estación"
        >
          <div className="w-14 h-14 rounded-full bg-[#0f1d6f] text-white flex items-center justify-center shadow-[0_12px_32px_rgba(15,29,111,0.35),0_4px_12px_rgba(28,28,28,0.15)] transition-colors group-hover:bg-[#0a1450]">
            <Camera size={22} strokeWidth={1.7}/>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-[#1c1c1c] bg-white/85 px-2 py-0.5 rounded-full">Capturar</span>
        </motion.button>
      )}
    </div>
  );
};

export default MobileRoute;
